import { useQuery } from '@tanstack/react-query';
import { getDashboardStats, getUsage } from '../api/client';
import OverviewCards from '../components/dashboard/OverviewCards';
import LiveVisitors from '../components/dashboard/LiveVisitors';
import TopPages from '../components/dashboard/TopPages';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { formatDate, formatRelativeTime } from '../utils/formatters';
import { COLORS } from '../utils/constants';
import { AlertTriangle, MousePointerClick } from 'lucide-react';
import InsightsSummary from '../components/insights/InsightsSummary';
import { Link } from 'react-router-dom';

export default function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => getDashboardStats({ dateRange: '7d' }),
  });

  const { data: usage } = useQuery({
    queryKey: ['usage'],
    queryFn: getUsage,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  const PIE_COLORS = ['#6366f1', '#8b5cf6', '#f59e0b', '#10b981'];

  // Normalize device breakdown to ensure consistent field names
  const deviceData = (stats?.deviceBreakdown || []).map((d: any) => ({
    name: d.deviceType || d.device || 'unknown',
    count: d.count || 0,
  }));

  return (
    <div className="space-y-6">
      {/* Usage Warning Banners */}
      {usage && usage.percentUsed >= 100 && (
        <div className="flex items-center gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
          <p className="flex-1 text-sm font-medium text-red-700">
            Session limit reached. New sessions are not being recorded.{' '}
            <Link to="/billing" className="underline hover:text-red-800">Upgrade your plan.</Link>
          </p>
        </div>
      )}
      {usage && usage.percentUsed >= 80 && usage.percentUsed < 100 && (
        <div className="flex items-center gap-3 rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-600" />
          <p className="flex-1 text-sm font-medium text-yellow-700">
            You've used {(usage.sessionCount || 0).toLocaleString()} of {(usage.sessionLimit || 0).toLocaleString()} sessions this month.{' '}
            <Link to="/billing" className="underline hover:text-yellow-800">Consider upgrading.</Link>
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <LiveVisitors />
      </div>

      {/* Row 1: Metric Cards */}
      <OverviewCards stats={stats} />

      {/* Row 2: Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Sessions Over Time */}
        <div className="card col-span-2">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Sessions Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.sessionsOverTime || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => formatDate(v)}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <Tooltip
                labelFormatter={(v) => formatDate(v as string)}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                }}
              />
              <Line
                type="monotone"
                dataKey={(entry: any) => entry.count ?? entry.sessions ?? 0}
                name="Sessions"
                stroke={COLORS.primary}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Device Breakdown */}
        <div className="card">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Device Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={deviceData}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={4}
              >
                {deviceData.map((_: any, i: number) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                formatter={(value) => (
                  <span className="text-xs text-gray-600">{value}</span>
                )}
              />
              <Tooltip
                formatter={(value: number) => [(value || 0).toLocaleString(), 'Sessions']}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Insights Summary */}
      <InsightsSummary />

      {/* Row 3: Top Pages + Top Referrers */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <TopPages pages={stats?.topPages || []} />

        {/* Top Referrers */}
        <div className="card">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Top Referrers</h3>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="table-header">Referrer</th>
                  <th className="table-header text-right">Sessions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(stats?.topReferrers || []).slice(0, 5).map((ref, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{ref.referrer || 'Direct'}</td>
                    <td className="table-cell text-right">{(ref.sessions ?? ref.count ?? 0).toLocaleString()}</td>
                  </tr>
                ))}
                {(!stats?.topReferrers || stats.topReferrers.length === 0) && (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center text-sm text-gray-500">
                      No referrer data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Row 4: Rage Clicks + Errors */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Rage Clicks */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <MousePointerClick className="h-4 w-4 text-orange-500" />
            <h3 className="text-sm font-semibold text-gray-900">Recent Rage Clicks</h3>
          </div>
          <div className="space-y-3">
            {(stats?.recentRageClicks || []).slice(0, 5).map((click, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-lg border border-gray-100 p-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 font-mono">{click.selector}</p>
                  <p className="text-xs text-gray-500">{click.page}</p>
                </div>
                <div className="text-right">
                  <span className="badge-warning">{click.count}x</span>
                  <p className="mt-1 text-xs text-gray-400">
                    {formatRelativeTime(click.lastSeen)}
                  </p>
                </div>
              </div>
            ))}
            {(!stats?.recentRageClicks || stats.recentRageClicks.length === 0) && (
              <p className="py-4 text-center text-sm text-gray-500">No rage clicks detected</p>
            )}
          </div>
        </div>

        {/* Recent Errors */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-semibold text-gray-900">Recent Errors</h3>
          </div>
          <div className="space-y-3">
            {(stats?.recentErrors || []).slice(0, 5).map((err, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-lg border border-gray-100 p-3"
              >
                <p className="flex-1 truncate text-sm font-medium text-gray-900">{err.message}</p>
                <div className="ml-4 text-right">
                  <span className="badge-danger">{err.count}x</span>
                  <p className="mt-1 text-xs text-gray-400">
                    {formatRelativeTime(err.lastSeen)}
                  </p>
                </div>
              </div>
            ))}
            {(!stats?.recentErrors || stats.recentErrors.length === 0) && (
              <p className="py-4 text-center text-sm text-gray-500">No recent errors</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
